import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { tx } from '../i18n';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { mensajeDe, pedir } from '../lib/api';
import { useSesion } from '../sesion/sesion';
import { Logo } from '../ui/Logo';

/** Solo se vuelve a una ruta propia del panel (nunca a otro sitio por un enlace armado). */
export function destinoSeguro(v: string | null): string {
  return v && v.startsWith('/') && !v.startsWith('//') && !v.startsWith('/ingresar') ? v : '/';
}

export function Ingreso() {
  const { usuario, ingresar } = useSesion();
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const [correo, setCorreo] = useState('');
  const [clave, setClave] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  // 'olvide': pide el enlace por correo; 'enviado': ya se pidió (la respuesta es siempre la misma)
  const [modo, setModo] = useState<'entrar' | 'olvide' | 'enviado'>('entrar');
  const volver = destinoSeguro(params.get('volver'));

  if (usuario) return <Navigate to={volver} replace />;

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setEnviando(true);
    try {
      await ingresar(correo.trim(), clave);
      navegar(volver, { replace: true });
    } catch (err) {
      // La API no dice si falló el correo o la clave, a propósito.
      setError(mensajeDe(err));
      setClave('');
    } finally {
      setEnviando(false);
    }
  };

  const pedirEnlace = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setEnviando(true);
    try {
      await pedir('/sesion/olvide', { metodo: 'POST', cuerpo: { correo: correo.trim() } });
      setModo('enviado');
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setEnviando(false);
    }
  };

  if (modo !== 'entrar')
    return (
      <PantallaDeIngreso>
        {modo === 'enviado' ? (
          <div className="ing-caja">
            <h1>{tx.ingreso.revise}</h1>
            <p className="ing-sub">{tx.ingreso.enviado(correo.trim())}</p>
            <button className="btn ing-boton" type="button" onClick={() => setModo('entrar')}>
              {tx.ingreso.volverAEntrar}
            </button>
          </div>
        ) : (
          <form className="ing-caja" onSubmit={pedirEnlace} noValidate>
            <h1>{tx.ingreso.olvideTitulo}</h1>
            <p className="ing-sub">{tx.ingreso.olvideQue}</p>
            <label className="campo" htmlFor="ing-correo">
              {tx.ingreso.correo}
              <input
                id="ing-correo"
                className="entrada"
                type="email"
                autoComplete="username"
                inputMode="email"
                placeholder={tx.ingreso.correoEjemplo}
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                required
                autoFocus
              />
            </label>
            {error && (
              <p className="error-form" role="alert">
                {error}
              </p>
            )}
            <button className="btn prim ing-boton" disabled={enviando || !correo.includes('@')}>
              {enviando ? tx.ingreso.mandando : tx.ingreso.mandarEnlace}
            </button>
            <button className="ing-enlace" type="button" onClick={() => setModo('entrar')}>
              {tx.ingreso.volverAEntrar}
            </button>
          </form>
        )}
      </PantallaDeIngreso>
    );

  return (
    <PantallaDeIngreso>
        <form className="ing-caja" onSubmit={enviar} noValidate>
          <h1>{tx.ingreso.titulo}</h1>
          <p className="ing-sub">{tx.ingreso.interno}</p>
          {params.get('vencida') && !error && (
            <p className="aviso-sesion" role="status">
              {tx.ingreso.vencida}
            </p>
          )}
          <label className="campo" htmlFor="ing-correo">
            {tx.ingreso.correo}
            <input
              id="ing-correo"
              className="entrada"
              type="email"
              autoComplete="username"
              inputMode="email"
              placeholder={tx.ingreso.correoEjemplo}
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="campo" htmlFor="ing-clave">
            {tx.ingreso.clave}
            <input
              id="ing-clave"
              className="entrada"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              required
            />
          </label>
          {error && (
            <p className="error-form" role="alert">
              {error}
            </p>
          )}
          <button className="btn prim ing-boton" disabled={enviando || !correo || !clave}>
            {enviando ? tx.ingreso.entrando : tx.ingreso.entrar}
          </button>
          <button
            className="ing-enlace"
            type="button"
            onClick={() => {
              setError('');
              setModo('olvide');
            }}
          >
            {tx.ingreso.olvide}
          </button>
        </form>
    </PantallaDeIngreso>
  );
}

/** La mitad de marca y el lado del formulario: la misma cara para entrar y para la clave nueva. */
function PantallaDeIngreso({ children }: { children: ReactNode }) {
  return (
    <div className="ingreso">
      {/* mitad de marca: el radar, qué es este panel y la firma; la misma cara de los 360 */}
      <section className="ing-marca">
        <div className="ing-logo">
          <Logo />
          <span className="ing-nombre">
            KNOC<b>360</b> {tx.ingreso.marca}
          </span>
        </div>
        <div className="ing-claim">
          <h2>{tx.ingreso.lema}</h2>
          <p>{tx.ingreso.que}</p>
        </div>
        <p className="ing-firma">
          by <span>Kuanta Bridge</span>
        </p>
      </section>

      <section className="ing-lado">
        {children}
        <p className="ing-pie">
          <b>
            KNOC<i>360</i>
          </b>
          {tx.ingreso.pie}
        </p>
      </section>
    </div>
  );
}

/**
 * La clave nueva, desde el enlace del correo (`/clave?t=…`). Primero se pregunta si el enlace
 * sirve: un formulario para un enlace vencido solo sirve para frustrar.
 */
export function NuevaClave() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [vale, setVale] = useState<boolean | null>(null);
  const [clave, setClave] = useState('');
  const [otra, setOtra] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [lista, setLista] = useState(false);
  const t = tx.ingreso;

  useEffect(() => {
    let vivo = true;
    pedir<{ vale: boolean }>(`/sesion/olvide/${encodeURIComponent(token)}`)
      .then((r) => vivo && setVale(r.vale))
      .catch(() => vivo && setVale(false));
    return () => {
      vivo = false;
    };
  }, [token]);

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    if (clave.length < 10) return setError(t.corta);
    if (clave !== otra) return setError(t.noCoinciden);
    setError('');
    setEnviando(true);
    try {
      await pedir('/sesion/olvide/clave', { metodo: 'POST', cuerpo: { token, clave_nueva: clave } });
      setLista(true);
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <PantallaDeIngreso>
      {lista ? (
        <div className="ing-caja">
          <h1>{t.claveLista}</h1>
          <p className="ing-sub">{t.claveListaQue}</p>
          <a className="btn prim ing-boton" href="/ingresar">
            {t.entrar}
          </a>
        </div>
      ) : vale === false ? (
        <div className="ing-caja">
          <h1>{t.enlaceMuerto}</h1>
          <p className="ing-sub">{t.enlaceMuertoQue}</p>
          <a className="btn prim ing-boton" href="/ingresar">
            {t.volverAEntrar}
          </a>
        </div>
      ) : (
        <form className="ing-caja" onSubmit={guardar} noValidate>
          <h1>{t.nuevaTitulo}</h1>
          <p className="ing-sub">{t.nuevaQue}</p>
          <label className="campo" htmlFor="nc-clave">
            {t.nueva}
            <input
              id="nc-clave"
              className="entrada"
              type="password"
              autoComplete="new-password"
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              disabled={vale === null}
              autoFocus
            />
          </label>
          <label className="campo" htmlFor="nc-otra">
            {t.repetir}
            <input
              id="nc-otra"
              className="entrada"
              type="password"
              autoComplete="new-password"
              value={otra}
              onChange={(e) => setOtra(e.target.value)}
              disabled={vale === null}
            />
          </label>
          {error && (
            <p className="error-form" role="alert">
              {error}
            </p>
          )}
          <button className="btn prim ing-boton" disabled={enviando || vale !== true || !clave || !otra}>
            {enviando ? t.guardando : t.guardarClave}
          </button>
        </form>
      )}
    </PantallaDeIngreso>
  );
}
